import { TiptapContent } from "@/data/use-documents-data";
import { getBasicExtensions } from "@nnr/tiptap-extensions";
import {
  CollabHistory,
  CollabHistoryVersion,
} from "@tiptap-pro/extension-collaboration-history";
import { CommentsKit } from "@tiptap-pro/extension-comments";
import { Collaboration } from "@tiptap/extension-collaboration";
import { CollaborationCursor } from "@tiptap/extension-collaboration-cursor";
import { Extension, useEditor } from "@tiptap/react";
import { useEffect, useRef, useState } from "react";
import { Markdown } from "tiptap-markdown";
import { useAuthContext } from "../../context/auth-context";
import {
  AuthenticatedImageNodeWithView,
  LibraryImageNodeWithView,
  MermaidCodeBlockNodeWithView,
  SectionTitleNodeWithView,
  YouTubeVideoNodeWithView,
} from "./nodes";
import { useCollabProvider } from "./use-collab-provider";

export function useCollabEditor(
  documentUuid: string | null,
  path: string | null,
  params?: UseCollabEditorOptions,
) {
  const { authDetails } = useAuthContext();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [versionHistory, setVersionHistory] = useState<VersionHistory>({
    versions: [],
    currentVersion: 0,
    latestVersion: 0,
  });
  const { provider, document, token } = useCollabProvider(documentUuid, path);

  useEffect(() => {
    if (!provider) {
      return;
    }
    const forceAutoVersioningOn = () => {
      provider.document
        .getMap("__tiptapcollab__config")
        .set("autoVersioning", 1);
    };
    provider.on("synced", forceAutoVersioningOn);
    return () => {
      provider.off("synced", forceAutoVersioningOn);
    };
  }, [provider]);

  const name = authDetails?.name ?? "";
  const color = useUserColor(name);
  const { editor, isWriting } = useCustomEditor({
    ...params,
    deps: [path, provider],
    collaborationEnabled: !!(provider && document),
    extensions:
      provider && document
        ? [
            Collaboration.configure({ document }),
            CollaborationCursor.configure({
              provider,
              user: {
                name,
                color: color.hex,
              },
            }),
            CommentsKit.configure({
              provider,
              useLegacyWrapping: false,
              onClickThread(id) {
                setSelectedThreadId(id);
              },
              deleteUnreferencedThreads: true,
            }),
            CollabHistory.configure({
              provider,
              onUpdate: (payload) => {
                setVersionHistory({
                  versions: payload.versions,
                  currentVersion: payload.currentVersion,
                  latestVersion: payload.version,
                });
              },
            }),
          ]
        : [],
  });

  return {
    editor,
    provider,
    document,
    isWriting,
    selectedThreadId,
    versionHistory,
    isConnected:
      (!!token &&
        provider &&
        provider.isConnected &&
        provider.isAuthenticated &&
        provider.isSynced) ??
      false,
  };
}

export type UseCollabEditorOnUpdate = (tiptapContent: TiptapContent) => void;

export type VersionHistory = {
  versions: CollabHistoryVersion[];
  currentVersion: number;
  latestVersion: number;
};

type UseCollabEditorOptions = {
  placeholder?: string;
  writingDebounceMs?: number;
  extensions?: Extension[];
  collaborationEnabled?: boolean;
  deps?: unknown[];
  onUpdate?: UseCollabEditorOnUpdate;
  onInitialize?: UseCollabEditorOnUpdate;
};

function useCustomEditor(params?: UseCollabEditorOptions) {
  const [isWriting, setIsWriting] = useState(false);
  const writingTimeout = params?.writingDebounceMs ?? 2000;
  const initializedRef = useRef(false);
  const previousContentRef = useRef<null | string>(null);

  const extensions = [
    ...getBasicExtensions({
      placeholder: params?.placeholder,
      history: !params?.collaborationEnabled,
    }),
    MermaidCodeBlockNodeWithView,

    // Custom Nodes with React Views
    YouTubeVideoNodeWithView,
    SectionTitleNodeWithView,
    AuthenticatedImageNodeWithView,
    LibraryImageNodeWithView,
    Markdown.configure({
      transformCopiedText: true,
      transformPastedText: true,
    }),

    ...(params?.extensions ?? []),
  ];

  const editor = useEditor(
    {
      immediatelyRender: false,
      extensions: extensions,
      editorProps: {
        attributes: {
          "aria-label": "Text Input",
        },
        // Custom implementation of pasting of images only. Text pasting is handled by default.
        handlePaste: (_: unknown, event: ClipboardEvent) => {
          if (event.clipboardData?.types.includes("Files")) {
            return true;
          }
        },

        handleDrop: (_: unknown, event: DragEvent) => {
          if (event.dataTransfer?.types.includes("Files")) {
            return true;
          }
        },
      },
      autofocus: true,
      editable: true,
      injectCSS: false,
      onUpdate: ({ editor }) => {
        if (editor.getHTML() === previousContentRef.current) {
          return;
        }
        previousContentRef.current = editor.getHTML();
        setIsWriting(true);
      },
    },
    params?.deps ?? [],
  );

  useEffect(() => {
    if (!editor) return;

    if (isWriting) {
      const timer = setTimeout(() => {
        setIsWriting(false);
        if (params) {
          if (!initializedRef.current && params.onInitialize) {
            params.onInitialize(editor.getJSON() as TiptapContent);
          } else {
            params.onUpdate?.(editor.getJSON() as TiptapContent);
          }
        }
        initializedRef.current = true;
      }, writingTimeout);

      return () => clearTimeout(timer);
    }
  }, [editor, isWriting, writingTimeout, params]);

  return { editor, isWriting };
}

// ------------------------------------------
// Temporarily copied from ui/src/hooks/useUserColor.ts this will be removed
// as user colors are going to be part of the user profile.

type UserColor = {
  hex: string;
  backgroundColor: string;
  borderColor: string;
  backgroundColorLight: string;
  textColor: string;
};

const COLOR_OPTIONS = [
  {
    hex: "#93C5FD",
    backgroundColor: "bg-blue-300",
    borderColor: "border-blue-300",
    backgroundColorLight: "bg-blue-100",
    textColor: "text-blue-300",
  },
  {
    hex: "#A5B4FC",
    backgroundColor: "bg-indigo-300",
    borderColor: "border-indigo-300",
    backgroundColorLight: "bg-indigo-100",
    textColor: "text-indigo-300",
  },
  {
    hex: "#7C2D12",
    backgroundColor: "bg-orange-700",
    borderColor: "border-orange-700",
    backgroundColorLight: "bg-orange-300",
    textColor: "text-orange-700",
  },
  {
    hex: "#4ADE80",
    backgroundColor: "bg-green-400",
    borderColor: "border-green-400",
    backgroundColorLight: "bg-green-100",
    textColor: "text-green-400",
  },
  {
    hex: "#F97316",
    backgroundColor: "bg-orange-500",
    borderColor: "border-orange-500",
    backgroundColorLight: "bg-orange-100",
    textColor: "text-orange-500",
  },
] as const;

const getColor = (seed = "") => {
  const score = seed
    .toLowerCase()
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return COLOR_OPTIONS[score % COLOR_OPTIONS.length];
};

/**
 * Hook that generates a consistent color based on a username string
 * @param username - The username to generate a color for
 * @returns A consistent color based on the username
 */
const useUserColor = (username: string): UserColor => {
  const color = getColor(username);
  return {
    hex: color.hex,
    backgroundColor: color.backgroundColor,
    borderColor: color.borderColor,
    backgroundColorLight: color.backgroundColorLight,
    textColor: color.textColor,
  };
};
